import { TFunction } from 'i18next';

import { FitnessApplicationActivityType } from '../../../../../types/__generated__';
import { validateDurationPart } from '.././Helpers/DurationHelpers';
import { validateTime } from '.././Helpers/TimeHelpers';
import { ManualFitnessEntryFormValues } from '../ManualFitnessEntryForm';
import { isNonDecimalNumber } from './NumberHelpers';
import {
  getActivityTypes,
  getTimePeriods,
  getUnitsOfMeasure,
} from './SelectItemBuilders';

export type ManualFitnessEntryFormErrors = Partial<
  Record<keyof ManualFitnessEntryFormValues | 'duration', string>
>;

export const buildValidator = (
  t: TFunction,
  activityTypes: ReturnType<typeof getActivityTypes>,
  distanceUnits: ReturnType<typeof getUnitsOfMeasure>,
  elevationUnits: ReturnType<typeof getUnitsOfMeasure>,
  timePeriods: ReturnType<typeof getTimePeriods>,
  childFormSections?: string[] | null,
) => (values: ManualFitnessEntryFormValues) => {
  const errors: ManualFitnessEntryFormErrors = {};

  if (values.durationHours && !validateDurationPart(values.durationHours)) {
    errors.durationHours = t('manualFitness.duration.validation.invalid');
  }

  if (values.durationMinutes && !validateDurationPart(values.durationMinutes)) {
    errors.durationMinutes = t('manualFitness.duration.validation.invalid');
  }

  if (values.durationSeconds && !validateDurationPart(values.durationSeconds)) {
    errors.durationSeconds = t('manualFitness.duration.validation.invalid');
  }

  if (!values.title) {
    errors.title = t('manualFitness.title.validation.required');
  } else if (values.title.trim() === '') {
    errors.title = t('manualFitness.title.validation.invalid');
  }

  if (childFormSections?.includes('ActivityTypes')) {
    if (!values.activityType) {
      errors.activityType = t('manualFitness.activityType.validation.required');
    } else if (!activityTypes.map(x => x.value).includes(values.activityType)) {
      errors.activityType = t('manualFitness.activityType.validation.invalid');
    }
  }

  if (!values.distanceUnit) {
    errors.distanceUnit = t('manualFitness.unitOfMeasure.validation.required');
  } else if (!distanceUnits.map(x => x.value).includes(values.distanceUnit)) {
    errors.distanceUnit = t('manualFitness.unitOfMeasure.validation.invalid');
  }

  if (values.distance && values.distance < 0) {
    errors.distance = t('manualFitness.distance.validation.invalid');
  }

  if (values.activityType !== FitnessApplicationActivityType.Swim) {
    if (!values.elevationUnit) {
      errors.elevationUnit = t(
        'manualFitness.unitOfMeasure.validation.required',
      );
    } else if (
      !elevationUnits.map(x => x.value).includes(values.elevationUnit)
    ) {
      errors.elevationUnit = t(
        'manualFitness.unitOfMeasure.validation.invalid',
      );
    }

    if (values.elevation && values.elevation < 0) {
      errors.elevation = t('manualFitness.elevation.validation.invalid');
    }
  }

  if (!values.activityDate) {
    errors.activityDate = t('manualFitness.activityDate.validation.invalid');
  }
  if (!validateTime(values.activityTime)) {
    errors.activityTime = t('manualFitness.activityDate.validation.invalid');
  }
  if (
    !values.activityTimePeriod ||
    !timePeriods.map(x => x.value).includes(values.activityTimePeriod)
  ) {
    errors.activityTimePeriod = t(
      'manualFitness.activityDate.validation.invalid',
    );
  }

  if (childFormSections?.includes('Steps')) {
    if (values.steps === undefined) {
      errors.steps = t('manualFitness.steps.validation.required');
    } else if (!isNonDecimalNumber(values.steps)) {
      errors.steps = t('manualFitness.steps.validation.invalid');
    } else if (values.steps < 1) {
      errors.steps = t('manualFitness.steps.validation.invalid');
    }
  }

  return errors;
};
